import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faArrowsRotate, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { CSSProperties, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { InGameCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandS, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { FragmentOf, unmaskFragment } from '@sorare/core/src/lib/graphql';
import { qualityNames } from '@sorare/core/src/lib/players';

import { Shape } from '../Shape';
import type { Slot_probabilisticBundleSlotConfig } from './__generated__/index.graphql';

const Content = styled(Vertical).attrs({
  center: true,
})`
  width: 100%;
  padding: var(--double-unit) 0;
`;
const Corner = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

const Layout = ({
  percent,
  big,
  illustration,
  description,
}: {
  percent: number;
  big?: boolean;

  illustration?: string | React.ReactNode;
  description?: React.ReactNode;
}) => {
  const Text = big ? BrandL : BrandS;
  return (
    <Content
      style={{
        width: big ? '200px' : undefined,
        height: big ? '317px' : undefined,
      }}
    >
      {big ? null : (
        <Corner>
          <LabelS color="var(--c-nd-500)">
            <FormattedNumber
              value={percent}
              style="percent"
              maximumFractionDigits={2}
            />
          </LabelS>
        </Corner>
      )}

      {typeof illustration === 'string' ? (
        <ResponsiveImg
          src={illustration}
          alt=""
          width={big ? 120 : 60}
          cropWidth={big ? 160 : 80}
        />
      ) : (
        illustration
      )}
      <Text>{description}</Text>
    </Content>
  );
};

type Props = {
  config: FragmentOf<Slot_probabilisticBundleSlotConfig>;
  big?: boolean;
  style?: CSSProperties;
};

export const Slot = ({ config: config_, big, style }: Props) => {
  const config = unmaskFragment(config_);
  // NOTHING
  let content: ReactNode = (
    <Layout
      percent={config.probability}
      big={big}
      illustration={
        <Vertical
          center
          style={{ aspectRatio: 1, width: big ? '120px' : '60px' }}
        >
          <FontAwesomeIcon icon={faTimes} fontSize={big ? '100px' : '60px'} />
        </Vertical>
      }
      description={
        <FormattedMessage id="wheel.slot.nothing" defaultMessage="NOTHING" />
      }
    />
  );

  // jersey
  // ticket
  if (config.deliverableItems?.length) {
    const deliverableItem = config.deliverableItems[0];

    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration={deliverableItem.deliverableItem.pictureUrl}
        description={
          <FormattedMessage
            id="wheel.slot.deliverableItem"
            defaultMessage="JERSEY"
          />
        }
      />
    );
  }

  // card
  if (config.cards?.length) {
    const card = config.cards[0];
    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration="https://frontend-assets.sorare.com/rewards/card-limited.png"
        description={!!card.quality && qualityNames[card.quality]}
      />
    );
  }
  // card shard
  if (config.cardShards?.length) {
    const cardShard = config.cardShards[0];
    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration="https://frontend-assets.sorare.com/rewards/shard-limited.png"
        description={cardShard.quantity}
      />
    );
  }

  // conversion credit
  if (config.conversionCredit) {
    content = (
      <Layout
        percent={config.probability}
        big={big}
        illustration="https://frontend-assets.sorare.com/rewards/credit.png"
        description={
          <FormattedNumber
            value={config.conversionCredit.percentageDiscount}
            style="percent"
          />
        }
      />
    );
  }
  if (config.inGameCurrencies?.length) {
    const igc = config.inGameCurrencies.filter(
      x => x.currency !== InGameCurrency.GOLDEN_WHEEL_TICKET
    )[0];
    // XP
    if (igc?.currency === InGameCurrency.LIMITED_XP) {
      content = (
        <Layout
          percent={config.probability}
          big={big}
          illustration="https://frontend-assets.sorare.com/rewards/xp-limited.png"
          description={igc.amount}
        />
      );
    }
    // respin
    if (igc?.currency === InGameCurrency.WHEEL_TICKET) {
      content = (
        <Layout
          percent={config.probability}
          big={big}
          illustration={
            <Vertical
              center
              style={{ aspectRatio: 1, width: big ? '120px' : '60px' }}
            >
              <FontAwesomeIcon
                icon={faArrowsRotate}
                fontSize={big ? '100px' : '60px'}
              />
            </Vertical>
          }
          description={
            <FormattedMessage id="wheel.slot.respin" defaultMessage="RESPIN" />
          }
        />
      );
    }
  }
  return (
    <Shape config={config} style={style}>
      {content}
    </Shape>
  );
};

Slot.fragments = {
  ProbabilisticBundleSlotConfig: gql`
    fragment Slot_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      probability
      ...Shape_probabilisticBundleSlotConfig

      deliverableItems {
        quantity
        deliverableItem {
          slug
          pictureUrl
          name
        }
      }
      cards {
        quality
      }
      rewardShopItems {
        quantity
        shopItem {
          id
          position
          name
        }
      }
      conversionCredit {
        id
        maxDiscount {
          usdCents
        }
        percentageDiscount
      }
      inGameCurrencies {
        id
        amount
        currency
      }
      cardShards {
        id
        quantity
        rarity
      }
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}
  ` as TypedDocumentNode<Slot_probabilisticBundleSlotConfig>,
};
